/**
 * Provides a way to access the currently active stateParams, which
 * are different during a stateChange than they are afterwards.
 */
import { toString } from '../../utils/string-utils';

export const uiRouterActiveParamsHelperModule = angular
  .module('Q.helpers.ui-router-active-params', [])

  .config(function ($provide, $stateProvider) {
    // gets a copy of the internal method from ui-router used to look up a parent
    // Note: this method has nothing to do with normal decorators, it's internal to ui-router.
    const parentGetter = $stateProvider.decorator('parent');

    return $provide.decorator('$state', function ($delegate, $rootScope) {
      let activeState = null;
      let activeParams = null;

      // prevent throwing an error on `go` if `state` is null or undefined
      const _go = $delegate.go;
      $delegate.go = (state, ...args) => {
        if (state === null || state === undefined) return;
        return _go(state, ...args);
      };

      /**
       * Returns true if the parentState is a parent of childState.
       * @param parentState {String|Object} parent ui state
       * @param childState {String|Object} child ui state
       * @returns {boolean}
       */
      $delegate.isParent = (parentState, childState) => {
        if (angular.isString(parentState))
          parentState = $delegate.get(parentState);
        if (!parentState || !parentState.name) return false;

        if (angular.isString(childState))
          childState = $delegate.get(childState);

        while (childState && childState.name) {
          if (parentState.name === childState.name) return true;
          childState = parentGetter(childState);
        }
        return false;
      };
      const isParent = $delegate.isParent;

      /**
       * Returns the correct active or current parameters for the provided state.
       * If the state isn't currently loaded or being loaded, returns false
       * @param state {String|Object} State we're trying to get parameters for
       * @param filter {boolean} If true, filter the result to only the params defined on the state
       * @param exclude {Array} A list of additional keys to exclude
       * @returns {Object|false} Parameters if we have them, false otherwise
       */
      $delegate.paramsFor = (state, filter = false, exclude = []) => {
        let params = false;
        if (activeState === state || isParent(state, activeState)) {
          // use the active params by default, for when we're switching state params only
          params = activeParams;
        } else if ($delegate.includes(state)) {
          params = $delegate.params;
        }
        if (params && filter) {
          let paramKeys = Object.keys($delegate.get(state).params);
          let newParams = {};
          paramKeys
            .filter((k) => exclude.indexOf(k) === -1)
            .forEach((k) => {
              newParams[k] = params[k];
            });
          params = newParams;
        }
        return params;
      };

      $delegate.toggleParam = (name, value, defaultValue = null) => {
        return angular.equals(
          toString($delegate.activeParams[name]),
          toString(value),
        )
          ? defaultValue
          : value;
      };

      Object.defineProperties($delegate, {
        /**
         * Returns the active state name. If we're in-transition, it's the toState, otherwise
         * it's $state.current.name.
         */
        activeState: {
          get() {
            return activeState || $delegate.current.name;
          },
        },
        /**
         * Returns the active state parameters. If we're in-transition, it's the toParams, otherwise
         * it's $state.params.
         */
        activeParams: {
          get() {
            return activeParams || $delegate.params;
          },
        },
      });

      $rootScope.$on('$stateChangeStart', function (event, toState, toParams) {
        if (!event.defaultPrevented) {
          activeState = toState.name;
          activeParams = toParams;

          if ($delegate.transition) {
            $delegate.transition.finally(clear);
          }
        }
      });

      function clear() {
        activeState = null;
        activeParams = null;
      }

      $rootScope.$on('$stateChangeSuccess', clear);

      $rootScope.$on('$stateChangeError', clear);

      return $delegate;
    });
  });
